import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, HelpCircle,
  Ship, Anchor, Train, FileSearch, Clock, Zap,
} from 'lucide-react';
import api from '../lib/api';
import FerrovalleCredentialsPanel from './FerrovalleCredentialsPanel';

const ICONS = {
  carrier: Ship,
  port: Anchor,
  ferrovalle: Train,
  soia: FileSearch,
};

const STATUS_STYLE = {
  OK: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', Icon: CheckCircle2, label: 'OK' },
  WARNING: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', Icon: AlertTriangle, label: 'Atención' },
  CRITICAL: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', Icon: XCircle, label: 'Crítico' },
  UNKNOWN: { color: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/30', Icon: HelpCircle, label: 'Sin datos' },
};

function formatRelative(iso) {
  if (!iso) return 'Nunca';
  try {
    const dt = new Date(iso);
    const diffMs = Date.now() - dt.getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return 'hace unos segundos';
    if (min < 60) return `hace ${min} min`;
    const hours = Math.round(min / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.round(hours / 24);
    return `hace ${days} d`;
  } catch {
    return iso;
  }
}

export default function ScraperHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const [backfilling, setBackfilling] = useState(false);
  const [lastBackfill, setLastBackfill] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/diagnostics/scraper-health');
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar la salud de scrapers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 30s so the panel reflects retries without manual reload
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRetry = async (name) => {
    setRetrying((prev) => ({ ...prev, [name]: true }));
    try {
      await api.post(`/diagnostics/scraper-health/${name}/retry`);
      toast.success(`Reintentando ${name}…`, { description: 'El panel se actualizará automáticamente' });
      // Poll a few times to catch the result
      setTimeout(load, 5000);
      setTimeout(load, 15000);
      setTimeout(load, 30000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `No se pudo reintentar ${name}`);
    } finally {
      setRetrying((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleBackfillSoia = async () => {
    if (backfilling) return;
    setBackfilling(true);
    try {
      const res = await api.post('/diagnostics/soia/backfill-all');
      const s = res.data || {};
      setLastBackfill(s);
      toast.success('Backfill SOIA completado', {
        description: `${s.containers_touched || 0} contenedores · ${s.events_inserted || 0} eventos · ${s.status_promoted || 0} estatus`,
        duration: 8000,
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo aplicar el backfill SOIA');
    } finally {
      setBackfilling(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="bg-card border border-border rounded-sm p-6 flex items-center justify-center" data-testid="scraper-health-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const ferroLoginFailed = data.ferrovalle_login_failed_today;

  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="scraper-health-card">
      <div className="bg-secondary/30 px-4 py-3 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="font-medium flex items-center gap-2">
            <Clock size={16} className="text-blue-400" />
            Salud del Tracking
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">Estado en tiempo real de los 4 scrapers (auto-refresh 30s)</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="scraper-health-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </Button>
      </div>

      {ferroLoginFailed && (
        <div className="bg-red-500/10 border-b border-red-500/30 px-4 py-3 flex items-start gap-3" data-testid="ferrovalle-login-alert">
          <XCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-300">Ferrovalle SLI: error de login HOY</p>
            <p className="text-xs text-muted-foreground mt-0.5 break-words">
              {data.ferrovalle_login_failure_message || 'Actualiza la contraseña en el panel «Credenciales Ferrovalle SLI» abajo y prueba el login.'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatRelative(data.ferrovalle_login_failure_at)}
            </p>
          </div>
        </div>
      )}

      <div className="divide-y divide-border">
        {(data.scrapers || []).map((s) => {          const style = STATUS_STYLE[s.status] || STATUS_STYLE.UNKNOWN;
          const Icon = ICONS[s.scraper] || HelpCircle;
          const StatusIcon = style.Icon;
          const isRetrying = !!retrying[s.scraper];
          return (
            <div
              key={s.scraper}
              className="px-4 py-3 flex items-center gap-3"
              data-testid={`scraper-row-${s.scraper}`}
            >
              <div className={`p-2 rounded-sm ${style.bg} ${style.border} border shrink-0`}>
                <Icon size={18} className={style.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{s.label}</span>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${style.bg} ${style.color}`} data-testid={`scraper-status-${s.scraper}`}>
                    <StatusIcon size={12} />
                    {style.label}
                  </span>
                  {s.consecutive_failures > 0 && (
                    <span className="text-xs text-red-400">
                      {s.consecutive_failures} fallo{s.consecutive_failures !== 1 ? 's' : ''} seguido{s.consecutive_failures !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                  <span>Última corrida: <strong className="text-foreground">{formatRelative(s.last_run_at)}</strong></span>
                  <span>Último éxito: <strong className="text-foreground">{formatRelative(s.last_success_at)}</strong></span>
                  {s.last_run_success && s.last_items_found > 0 && (
                    <span className="text-emerald-400">{s.last_items_found} encontrados</span>
                  )}
                </div>
                {s.last_error_message && (
                  <p className="text-xs text-red-400 mt-1 break-words" data-testid={`scraper-error-${s.scraper}`}>
                    {s.last_error_message}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRetry(s.scraper)}
                disabled={isRetrying}
                data-testid={`scraper-retry-${s.scraper}`}
              >
                {isRetrying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                <span className="ml-1.5 hidden sm:inline">Reintentar</span>
              </Button>
            </div>
          );
        })}
      </div>

      <div className="bg-secondary/30 px-4 py-3 border-t border-border" data-testid="soia-backfill-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium flex items-center gap-1.5">
              <Zap size={14} className="text-amber-400" />
              Backfill SOIA retroactivo
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Aplica eventos al timeline y actualiza estatus de contenedores a partir de pedimentos ya consultados (sin gastar 2Captcha). Útil tras un deploy con cambios al motor SOIA.
            </p>
            {lastBackfill && (
              <p className="text-xs text-emerald-400 mt-1.5" data-testid="soia-backfill-last-result">
                Último resultado: {lastBackfill.containers_touched} contenedores · {lastBackfill.events_inserted} eventos · {lastBackfill.status_promoted} estatus actualizados ({lastBackfill.elapsed_seconds}s)
              </p>
            )}
          </div>
          <Button
            size="sm"
            variant="default"
            onClick={handleBackfillSoia}
            disabled={backfilling}
            data-testid="soia-backfill-btn"
          >
            {backfilling ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            <span className="ml-1.5">{backfilling ? 'Ejecutando…' : 'Aplicar backfill'}</span>
          </Button>
        </div>
      </div>

      <FerrovalleCredentialsPanel />
    </div>
  );
}
